import React, { useState, useEffect } from 'react';
import { WeatherButton, WeatherIcon, WeatherInfoColumn, TemperatureText, DescriptionText } from './styles';

interface WeatherProps {
  city: string;
  apiKey: string;
  hideOn?: 'mobile' | 'desktop';
}

interface WeatherData {
  temp: number;
  description: string;
  iconCode: string;
  cityId: number;
  cityName: string;
  timestamp: number;
}

const UNITS = 'metric';
const LANG = 'pt_br';
const ICON_BASE_URL = 'https://openweathermap.org/img/wn/';
const CACHE_KEY = 'weather_data_cache';
const CACHE_MINUTES = 30;

const WeatherHeaderButton: React.FC<WeatherProps> = ({ city, apiKey, hideOn }) => {
  const [weatherData, setWeatherData] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchWeather = async () => {
      const cached = localStorage.getItem(CACHE_KEY);

      if (cached) {
        const parsedCache: WeatherData = JSON.parse(cached);
        const now = new Date().getTime();
        const expiry = CACHE_MINUTES * 60 * 1000;

        if (now - parsedCache.timestamp < expiry && parsedCache.cityName.toLowerCase() === city.toLowerCase()) {
          setWeatherData(parsedCache);
          setLoading(false);
          return;
        }
      }

      try {
        const response = await fetch(
          `https://api.openweathermap.org/data/2.5/weather?q=${city}&units=${UNITS}&lang=${LANG}&appid=${apiKey}`
        );
        if (!response.ok) throw new Error();
        const data = await response.json();

        const newWeatherData: WeatherData = {
          temp: Math.round(data.main.temp),
          description: data.weather[0].description,
          iconCode: data.weather[0].icon,
          cityId: data.id,
          cityName: city,
          timestamp: new Date().getTime(),
        };

        localStorage.setItem(CACHE_KEY, JSON.stringify(newWeatherData));
        setWeatherData(newWeatherData);
      } catch (err) {
        setError('Erro');
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();
  }, [city, apiKey]);

  const handleButtonClick = () => {
    if (!weatherData) return;
    window.open(`https://openweathermap.org/city/${weatherData.cityId}`, '_blank');
  };

  if (error || !weatherData) return null;

  const iconUrl = `${ICON_BASE_URL}${weatherData.iconCode}@4x.png`;
  return (
    <WeatherButton onClick={handleButtonClick} hideOn={hideOn} title={`Ver previsão de 8 dias em ${city}`}>
      <WeatherIcon src={iconUrl} alt={weatherData.description} />

      <WeatherInfoColumn>
        <TemperatureText>{weatherData.temp}°C</TemperatureText>
        <DescriptionText>{weatherData.description}</DescriptionText>
      </WeatherInfoColumn>
    </WeatherButton>
  );
};

export default WeatherHeaderButton;
